import React from 'react';
import { GlobalStyles } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DropzoneProps, DropzoneState, useDropzone } from 'react-dropzone';
import { useAlert } from '../AlertProvider';

export { ErrorCode as DropzoneErrorCode } from 'react-dropzone';


export const createBorderBackground = (color: string) => `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='${color.replace('#', '%23')}' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`;


const useStyles = makeStyles<Partial<SimpleDropzoneProps>>()((theme, props) => ({
  root: {
    width: '100%',
    height: '100%',
    backgroundClip: 'content-box',
    backgroundImage: createBorderBackground(theme.palette[props.color ?? 'secondary'].main),
    color: theme.palette[props.color ?? 'secondary'].main,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  active: {
    backgroundImage: createBorderBackground(theme.palette[props.color ?? 'secondary'].dark),
    color: theme.palette[props.color ?? 'secondary'].dark,
    opacity: 0.8,
  },
  accept: {
    backgroundImage: createBorderBackground(theme.palette[props.color ?? 'secondary'].dark),
    color: theme.palette[props.color ?? 'secondary'].dark,
  },
  hoverActive: {
    '&:hover': {
      backgroundImage: createBorderBackground(theme.palette[props.color ?? 'secondary'].dark),
      color: theme.palette[props.color ?? 'secondary'].dark,
    }
  },
  reject: {
    backgroundImage: `${createBorderBackground(theme.palette.error.main)} !important`,
    color: `${theme.palette.error.main} !important`,
  },
}));


export type SimpleDropzoneProps = DropzoneProps & {
  error?: boolean;
  color?: 'primary' | 'secondary';
  children: (state: Omit<DropzoneState, 'getRootProps' | 'getInputProps'>) => React.ReactElement;
  activeOnHover?: boolean;
};


export const SimpleDropzone: React.VFC<SimpleDropzoneProps> = ({ children, ...props }) => {
  const alert = useAlert();
  const { classes, cx } = useStyles(props, { props });
  const { getRootProps, getInputProps, ...state } = useDropzone(props);
  const rejection = state.fileRejections?.[0];

  React.useEffect(() => {
    if (!rejection) return;
    const error = rejection.errors[0];

    if (!error) return;

    if (error.code === 'file-too-large' && props.maxSize) {
      alert.error(`File is too large! Maximum size is ${Math.round(props.maxSize / 1024 / 1024 * 100) / 100} megabytes.`);
      return;
    } else {
      alert.error(error.message);
    }
  }, [ rejection ]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={cx(classes.root, {
        [classes.active]: state.isDragActive,
        [classes.accept]: state.isDragAccept,
        [classes.hoverActive]: props.activeOnHover,
        [classes.reject]: state.isDragReject || props.error || !!rejection,
      })}
      {...getRootProps()}
    >
      <GlobalStyles
        styles={{
          iframe: {
            pointerEvents: 'none',
          }
        }}
      />
      <input {...getInputProps()} />
      {children(state)}
    </div>
  );
};
